import { MAX_ARCHIVE_ENTRIES, MAX_ARCHIVE_TOTAL_BYTES, buildArchive, makeCapsuleFile, type ArchiveCapsule, type CapsuleFile, type Event, type RandomnessBeacon } from "@timecapsule/crypto";
import { api } from "./api";
import { fetchFile } from "./attachments";
import { chainClient, openCapsule, type CapsuleView } from "./capsules";
import type { Opened } from "./storage";
import { msg } from "./i18n";
import { formatTime } from "./time";

/**
 * Keep capsules outside vrelis.com (PLAN.md 4.8). One capsule as a .json file, or several as a
 * .zip with their encrypted blobs, the offline opener and a README. After unlock a file also
 * carries the verified drand beacon of its round, so it no longer needs drand; on request also
 * the opened text and files, which are then plain on the member's disk.
 */

/**
 * The event worth keeping: a public capsule itself; for a private one the wrap addressed to one of
 * my keys, current or earlier (Inbox, or my "keep a copy" in Sent; PLAN.md 4.18 step 6). A capsule I
 * sent without a copy cannot be read by me even offline, so it has nothing to download.
 */
export function downloadableEvent(view: CapsuleView, myKeys: ReadonlySet<string>): { event: Event; blobs: string[] } | null {
  if (view.copy) return { event: view.copy.row.event, blobs: view.copy.row.blobs };
  if (!view.isPrivate) return { event: view.row.event, blobs: view.row.blobs };
  if (view.recipient && myKeys.has(view.recipient)) return { event: view.row.event, blobs: view.row.blobs };
  return null;
}

/** The verified beacon of the capsule's round once it exists; null before unlock or when unreachable. */
async function beaconFor(view: CapsuleView): Promise<RandomnessBeacon | null> {
  const round = view.copy?.round ?? view.round;
  const unlockAt = view.copy?.unlockAt ?? view.unlockAt;
  if (!round || !unlockAt || unlockAt > Date.now()) return null;
  try {
    return await chainClient().get(round);
  } catch {
    return null;
  }
}

/**
 * Opened content to include: from the cache, or opened now if the capsule has unlocked. Private
 * content only while the key is unlocked (PLAN.md 4.13 R3): the view may still hold text from
 * before a Lock.
 */
async function openedFor(view: CapsuleView, memberId: number, secretKey: Uint8Array | null): Promise<Opened | null> {
  const target = view.copy ?? view;
  if (target.isPrivate && !secretKey) return null;
  if (target.opened) return target.opened;
  if (!target.unlockAt || target.unlockAt > Date.now()) return null;
  try {
    return await openCapsule(view, memberId, secretKey);
  } catch {
    return null;
  }
}

export type DownloadOptions = {
  memberId: number;
  /** Every key of this account (session myKeys). */
  myKeys: ReadonlySet<string>;
  secretKey: Uint8Array | null;
  includeOpened: boolean;
  /** False once the key was locked (or unlocked again) after the download started. */
  stillUnlocked: () => boolean;
};

export class LockedDuringDownloadError extends Error {
  constructor() {
    super(msg().errors.lockedDuringDownload);
  }
}

/**
 * One archive is built whole in memory (fflate `zipSync`), so it is capped before it can freeze the
 * tab (reviews/06-action-plan.md 2.3). The count is checked before any request; the bytes of
 * attachments and opened files are counted as they arrive and the export stops before the next
 * fetch once the cap is passed. Both stay under what `readArchive` accepts, so the opener never
 * refuses an archive this app made.
 */
export const MAX_EXPORT_CAPSULES = 300;
export const MAX_EXPORT_BYTES = Math.min(160 * 1024 * 1024, MAX_ARCHIVE_TOTAL_BYTES / 2);

export class ExportTooLargeError extends Error {
  constructor(readonly reason: "count" | "bytes" | "entries") {
    super(reason === "count" ? msg().errors.exportTooMany(MAX_EXPORT_CAPSULES) : msg().errors.exportTooBig);
  }
}

/** Before asking for the key or fetching anything: too many capsules for one archive. */
export function checkExportCount(count: number): void {
  if (count > MAX_EXPORT_CAPSULES) throw new ExportTooLargeError("count");
}

async function capsuleEntry(view: CapsuleView, opts: DownloadOptions): Promise<{ entry: ArchiveCapsule; blobs: string[] } | null> {
  const picked = downloadableEvent(view, opts.myKeys);
  if (!picked) return null;
  const beacon = await beaconFor(view);
  const opened = opts.includeOpened ? await openedFor(view, opts.memberId, opts.secretKey) : null;
  const openedFiles = opened ? await Promise.all(opened.files.map(async (f) => ({ name: f.name, bytes: await fetchFile(f) }))) : undefined;
  if (opened && (view.copy ?? view).isPrivate && !opts.stillUnlocked()) throw new LockedDuringDownloadError();
  const file: CapsuleFile = makeCapsuleFile(picked.event, { ...(beacon ? { beacon } : {}), ...(opened ? { opened } : {}) });
  return { entry: { file, openedFiles }, blobs: picked.blobs };
}

/** One signed event as a capsule file (Try it); with the beacon of its round once that exists, so it opens offline. */
export function saveCapsuleEvent(event: Event, beacon: RandomnessBeacon | null = null): void {
  save(new TextEncoder().encode(JSON.stringify(makeCapsuleFile(event, beacon ? { beacon } : {}), null, 2)), `vrelis-${event.id.slice(0, 12)}.json`);
}

function save(bytes: Uint8Array, name: string) {
  const url = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const today = () => new Date().toISOString().slice(0, 10);

/** One capsule as vrelis-<id prefix>.json. Attachments need the archive. */
export async function downloadOne(view: CapsuleView, opts: DownloadOptions): Promise<void> {
  const built = await capsuleEntry(view, opts);
  if (!built) throw new Error(msg().errors.nothingToDownload);
  if (built.entry.file.opened && (view.copy ?? view).isPrivate && !opts.stillUnlocked()) throw new LockedDuringDownloadError();
  save(new TextEncoder().encode(JSON.stringify(built.entry.file, null, 2)), `vrelis-${built.entry.file.event.id.slice(0, 12)}.json`);
}

/**
 * One capsule from its row: a .json when it has no files, else a .zip with its blobs, the opener
 * and a README, so the files are never left behind (owner's finding, 4b step 1).
 */
export async function downloadCapsule(view: CapsuleView, opts: DownloadOptions): Promise<void> {
  const picked = downloadableEvent(view, opts.myKeys);
  if (picked && picked.blobs.length > 0) await downloadArchive([view], opts);
  else await downloadOne(view, opts);
}

/** Several capsules as one .zip with their blobs, the offline opener and a README. */
export async function downloadArchive(views: CapsuleView[], opts: DownloadOptions): Promise<{ count: number; skipped: number }> {
  checkExportCount(views.length);
  const capsules: ArchiveCapsule[] = [];
  const shas = new Set<string>();
  let skipped = 0;
  let privateOpened = false;
  let bytes = 0;
  let openedFiles = 0;
  const count = (n: number) => {
    bytes += n;
    if (bytes > MAX_EXPORT_BYTES) throw new ExportTooLargeError("bytes");
  };
  for (const view of views) {
    const built = await capsuleEntry(view, opts);
    if (!built) {
      skipped++;
      continue;
    }
    capsules.push(built.entry);
    for (const f of built.entry.openedFiles ?? []) count(f.bytes.length);
    openedFiles += built.entry.openedFiles?.length ?? 0;
    if (built.entry.file.opened && (view.copy ?? view).isPrivate) privateOpened = true;
    for (const sha of built.blobs) shas.add(sha);
  }
  if (capsules.length === 0) throw new Error(msg().errors.noneDownloadable);
  // Capsule files, blobs, opened files, the opener and the README: what readArchive will count.
  if (capsules.length + shas.size + openedFiles + 2 > MAX_ARCHIVE_ENTRIES) throw new ExportTooLargeError("entries");
  const blobs = new Map<string, Uint8Array>();
  for (const sha of shas) {
    const blob = await api.fetchBlob(sha);
    count(blob.length);
    blobs.set(sha, blob);
  }
  const opener = await fetch("/vrelis-opener.html", { credentials: "same-origin" }).then((r) => (r.ok ? r.text() : null), () => null);
  const folder = `vrelis-capsules-${today()}`;
  // The last check before anything reaches the disk: blobs and the opener took network time.
  if (privateOpened && !opts.stillUnlocked()) throw new LockedDuringDownloadError();
  save(buildArchive({ folder, capsules, blobs, openerHtml: opener, readme: readme(capsules.length, opts.includeOpened, !!opener) }), `${folder}.zip`);
  return { count: capsules.length, skipped };
}

/** README.txt of the archive, in the member's language (4b step 3b). */
function readme(count: number, includeOpened: boolean, withOpener: boolean): string {
  return msg()
    .archiveReadme(count, formatTime(Date.now()), includeOpened, withOpener)
    .filter((l, i, all) => l !== "" || all[i - 1] !== "")
    .join("\n");
}
